import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';

/**
 * Determines whether SMS is enabled for the current event.
 *
 * Evaluates four conditions:
 * - `smsChannelEnabled` (Amplitude) — global SMS rollout flag
 * - `enableSmsUpsellOptinOverride` (event feature flag) — per-event opt-in override set via Event Service Center
 * - `hasMessagingPurchase` — paid messaging plus users bypass the Amplitude flag
 * - `disableSmsUpsell` (event feature flag) — per-event kill switch set via Event Service Center
 *
 * Logic: `((smsChannelEnabled === 'on' || enableSmsUpsellOptinOverride || hasMessagingPurchase) && !disableSmsUpsell)`
 *
 * - Override or paid users get SMS even when the Amplitude flag is off
 * - `disableSmsUpsell` ON → SMS disabled for everyone, including override and paid users
 * - `disableSmsUpsell` OFF → Amplitude flag, override, or purchase controls SMS
 *
 * @param hasMessagingPurchase - Whether the event has an active messaging plus purchase
 * @returns `[isSmsEnabled, isLoading]` tuple
 */
export const useIsSmsEnabled = (hasMessagingPurchase: boolean): [isSmsEnabled: boolean, isLoading: boolean] => {
  const { value: smsChannelValue, loading: smsChannelLoading } = useFeatureValue('smsChannelEnabled');
  const [isOptinOverrideEnabled, optinOverrideLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'enableSmsUpsellOptinOverride' });
  const [isSmsUpsellDisabled, isSmsUpsellFlagLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'disableSmsUpsell' });

  const isLoading = smsChannelLoading || optinOverrideLoading || isSmsUpsellFlagLoading;
  const isSmsEnabled = (smsChannelValue === 'on' || isOptinOverrideEnabled || hasMessagingPurchase) && !isSmsUpsellDisabled;

  return [isSmsEnabled, isLoading];
};
